import { Box, Stack, Typography } from "@mui/material";

import type { StopKind } from "../../../utils/metro-stops";
import { LOCKED_LABEL } from "../../../utils/metro-stops";

import { lineColor, METRO_INK, METRO_SPUR } from "./metro-theme";
import { MetroStopIcon } from "./MetroStopIcon";

const MARKS: { kind: StopKind; label: string; background: string; color: string; dashed?: boolean }[] = [
    { kind: 'done', label: 'finished', background: '#3D7BEA', color: '#FFFFFF' },
    { kind: 'recap', label: 'recap due', background: '#FFC940', color: METRO_INK },
    { kind: 'current', label: 'up next', background: '#FFFFFF', color: '#FF6B4A' },
    { kind: 'open', label: 'not started', background: '#FFFFFF', color: '#3D7BEA' },
    { kind: 'locked', label: LOCKED_LABEL, background: '#FFFFFF', color: '#6B7785' },
    { kind: 'off', label: 'not enough questions', background: '#F4F6F8', color: '#6B7785', dashed: true },
];

export type MetroLegendProps = {
    units: string[];
};

/** Explains the lines and the stops of the map, which say little on their own. */
export function MetroLegend({ units }: MetroLegendProps) {
    return (
        <Stack spacing={ 1 } sx={ { fontSize: 13, color: '#4F5B67' } }>
            <Stack direction="row" flexWrap="wrap" alignItems="center" columnGap={ 2.25 } rowGap={ 1 }>
                { units.map((title, index) => (
                    <Stack key={ title } direction="row" alignItems="center" spacing={ 1 }>
                        <LineSample color={ lineColor(index) }/>
                        <Typography variant="body2"><strong>Unit { index + 1 }</strong> { title }</Typography>
                    </Stack>
                )) }
                <Stack direction="row" alignItems="center" spacing={ 1 }>
                    <svg width="34" height="14" aria-hidden="true">
                        <path d="M4 7 H30" stroke={ METRO_SPUR } strokeWidth="4" strokeLinecap="round" strokeDasharray="1 7"/>
                    </svg>
                    <Typography variant="body2">Bonus spur</Typography>
                </Stack>
                <Typography variant="body2">Parallel tracks: any order, all before the next stop</Typography>
            </Stack>

            <Stack role="list" aria-label="Lesson marks" direction="row" flexWrap="wrap" alignItems="center" columnGap={ 2 } rowGap={ 1 }>
                { MARKS.map((mark) => (
                    <Stack key={ mark.kind } role="listitem" direction="row" alignItems="center" spacing={ 0.75 }>
                        <Box aria-hidden="true" sx={ {
                            width: 22, height: 22, boxSizing: 'border-box', borderRadius: '50%', bgcolor: mark.background, color: mark.color,
                            border: `2px ${ mark.dashed ? 'dashed' : 'solid' } ${ METRO_INK }`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        } }>
                            <MetroStopIcon kind={ mark.kind } size={ 12 }/>
                        </Box>
                        <Typography variant="caption">{ mark.label }</Typography>
                    </Stack>
                )) }
            </Stack>
        </Stack>
    );
}

function LineSample({ color }: { color: string }) {
    return (
        <svg width="34" height="14" aria-hidden="true">
            <path d="M4 7 H30" stroke={ METRO_INK } strokeWidth="10" strokeLinecap="round"/>
            <path d="M4 7 H30" stroke={ color } strokeWidth="5" strokeLinecap="round"/>
        </svg>
    );
}
